import { Box, Button, Card, Chip, IconButton, Link, ListItemIcon, ListItemText, MenuItem, Tooltip } from "@mui/material";
import { FC, UIEvent, useEffect, useMemo, useRef, useState } from "react";

import { BorderColorOutlined, Download, Leaderboard, MoreVert, PlayCircle, Settings, Terminal, Timeline } from "@mui/icons-material";
import { isEmpty } from "lodash";
import MaterialReactTable from "material-react-table";
import { useHistory } from "react-router-dom";
import { API_SLUGS, ROUTES, TOOL_TYPES } from "../../../constants";
import { useTableInfinity } from "../../../hooks/process/useTableInfinity";
import { ExclamationCircle } from "../../../icons/exclamation-circle";
import { ColumnHeaderFilter } from "../../analysis/ViewIssues/IssuesTable";
import { ApiModalContent } from "../../assets/ApiModal";
import CommonPopper from "../../common/commonPopper";
import Metrics from "../../common/metrics";
import ModalWrapper from "../../common/modal/wrapper";
import TruncatedScrollingText from "../../common/truncatedScrollingText";
import ConfigTablePopup from "../../dashboard/configTablePopup";
import DownloadPostmanPopup from "../../dashboard/downloadPostmanPopup";
import ScannersPopup from "../../dashboard/scannersPopup";
import FaultGroups from "../../common/faultGroups";
import SeverityTimeSeries from "../../dashboard/widgetLibrary/severityTimeSeries";
import CategoryBar from "../../dashboard/widgetLibrary/categoryBar";
import AddProjectForm from "../AddProject/AddProjectForm";
import EnvironmentsTable from "../../management/ViewEnvironments/EnvironmentsTable";
import LinkProjectToJira from "../LinkProjectToJira";
import { useBreadcrumbState } from "../../../store/breadcrumbState";
import { axiosInstance } from "../../../services/axios";
import CloudIcon from '@mui/icons-material/Cloud';

const limit = 30;
const disableInfiniteScroll = false;
const ProjectTable: FC<any> = ({ groupId, projectId }) => {
  const path = useMemo(() => {
    let path = API_SLUGS.VIEW_PROJECTS + `?limit={limit}&offset={offset}`;
    if (groupId) {
      path += `&groupId=${groupId}`
    }
    if (projectId) {
      path += `&projectId=${projectId}`
    }

    return path
  }, [groupId, projectId]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const selectedGroup = useBreadcrumbState((state) =>
          //@ts-ignore
          state.selectedGroup)

  const [columnHeaderFilter, setColumnHeaderFilter] = useState<any>({});
  const [jiraDetails, setJiraDetails] = useState<any>();
  const projectsUrl = useMemo(() => {
    let newPath = path
    if (!isEmpty(columnHeaderFilter)) {
      for (let key in columnHeaderFilter) {
        const columnFilterValues = columnHeaderFilter[key];
        for (let value of columnFilterValues) {
          if (value !== null) {
            newPath += `&${key}=${value}`;
          }
        }
      }
    }
    return newPath;
  }
    , [columnHeaderFilter, path])
  const { rows, fetchMoreOnBottomReached, isFetching } = useTableInfinity(
    "projects",
    projectsUrl,
    tableContainerRef,
    limit
  );

  const assetOptions = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const uniqueMap = new Map();
    rows.forEach((row) => {
      if (row?.assetName) {
        uniqueMap.set(row.assetId, { key: row.assetId, label: row.assetName });
      }
    });
    
    return Array.from(uniqueMap.values())
    
  }, [rows]);


  const fetchProjectDetails = async (id: string) => {
    try {
      const path = API_SLUGS.GET_PROJECT.replace("{uuid}", id)
      const response = await axiosInstance.get(path);
      setJiraDetails(response?.data?.jira?.details)
    } catch (error) {
      
    }
  }



  const handleColumnHeaderFilterChange = (key: string, value: any[] | null) => {
    if (!isEmpty(value)) {
      setColumnHeaderFilter({ ...columnHeaderFilter, [key]: value! });
    } else {
      let newFilter = { ...columnHeaderFilter };
      delete newFilter[key];
      setColumnHeaderFilter({ ...newFilter });
    }
  };

  useEffect(() => {
    if (!disableInfiniteScroll) {
      fetchMoreOnBottomReached(tableContainerRef.current);
    }
  }, [fetchMoreOnBottomReached])

  const navigate = useHistory();
  return (
    <Card elevation={0} sx={{ textAlign: "left" }}>
      <MaterialReactTable muiTablePaperProps={{
        elevation: 0
      }} enableGlobalFilter={false}
        columns={[
          {
            accessorKey: "name",
            header: "Project",

            size: 130,
            Cell: (params: any) => {
              const path = ROUTES.PROJECT.replace(':projectId', params?.row?.original?.id);
              return (
                <Box style={{ fontWeight: "bold", display: "flex" }} className="hand">
                  <Link><TruncatedScrollingText typographyVariant="h6" onClick={() => navigate.push(path)} color={"text.primary"} name={params?.row?.original?.name} maxLength={15} labels={params?.row?.original?.labels} /></Link>
                </Box>
              )
            }, 
          },
          {
            accessorKey: "environmentName",
            header: "Environment",

            size: 100,
          },
          {
            accessorKey: "assetName",
            header: "Asset",
            Header: (
              <ColumnHeaderFilter
                columnKey="assetId"
                name="Asset"
                options={assetOptions}
                setColumnHeaderFilter={handleColumnHeaderFilterChange}
                columnHeaderFilter={columnHeaderFilter}
              />
            ),

            size: 100,
            Cell: (params: any) => {
              const path = ROUTES.PROJECT.replace(':projectId', params?.row?.original?.id) + `?assetid=${params?.row?.original?.assetId}`;
              return (
                <Link className="hand"><TruncatedScrollingText sx={{ color: "text.primary" }} typographyVariant='h6' onClick={() => navigate.push(path)} name={params?.row?.original?.assetName} labels={params?.row?.original?.assetLabels} maxLength={15} /></Link>
              )
            }
          },
          {
            accessorKey: "type",
            header: "Type",
            Header: (
              <ColumnHeaderFilter
                columnKey="type"
                name="Type"
                options={TOOL_TYPES}
                setColumnHeaderFilter={handleColumnHeaderFilterChange}
              />
            ),

            size: 50,
            Cell: (params: any) => (<>{params?.row?.original?.findingType && (<Chip
              label={params?.row?.original?.findingType.toUpperCase()}
              size="small"
              variant={"filled"}
              color="default"
            />)}</>)

          },
          {
            accessorKey: "metrics",
            header: "Open Issues By Risk",

            Cell: (params: any) => {
              return (
                <Metrics data={params?.row?.original?.incidentsCountByRisk} />
              )
            }
          },
          {
            accessorKey: "allIssues",
            header: "",
            Cell: (params: any) => {
              return (
                <Tooltip title='All Issues'>
                  <IconButton onClick={() =>
                      navigate.push(
                        `/api/${params?.row?.original?.assetId}/details?environmentId=${params?.row?.original?.environmentId}`
                      )
                    }
                  >
                      <ExclamationCircle />
                    </IconButton>
                </Tooltip>
              )
            },
            size: 80
          },
          {
            accessorKey: "latestResultIssuesCountByRisk",
            header: "Latest Run Issues By Risk",

            Cell: (params: any) => {
              return (
                <Metrics data={params?.row?.original?.latestResultIssuesCountByRisk} />
              )
            }
          },
          {
            accessorKey: "latestRun",
            header: "",

            Cell: (params: any) => {
              return (
                params?.row?.original?.latestResultId && 
                <Tooltip title='Latest Run'>
                  <IconButton onClick={() =>
                      navigate.push(
                        `/findings?resultId=${params?.row?.original?.latestResultId}`
                      )
                    }
                  >
                      <ExclamationCircle />
                    </IconButton>
                </Tooltip>
              )
            },
            size: 80
          },
          {
            accessorKey: "charts",
            header: "Charts",
            size: 100,
            Cell: (params: any) => {
              const data = params?.row?.original;
              return (<Box sx={{ display: "flex", justifyContent: "start" }}>

                <CommonPopper
                  disableHideOnClick={true}
                  trigger={
                    <Tooltip title="Trends">
                      <IconButton>
                        <Timeline />
                      </IconButton>
                    </Tooltip>
                  }
                  child={
                    <>

                      <Box sx={{ width: "30em", p:2, pb: 4, pr: 4}}>
                        <SeverityTimeSeries groupId={data?.groupId} projectId={data?.id} height="220px" environmentId={data?.environmentId} />
                        {/* <SeverityTimeSeries projectId={params?.row?.original?.id} environmentId={params?.row?.original?.environmentId} defaultSelectedIssueType={"open"} height="250px" /> */}
                      </Box>
                    </>
                  }
                />
                <CommonPopper
                  disableHideOnClick={true}
                  trigger={
                    <Tooltip title="Categories">
                      <IconButton>
                        <Leaderboard />
                      </IconButton>
                    </Tooltip>
                  }
                  child={
                    <>

                      <Box sx={{ minWidth: "30em", p: 2 }}>
                         <CategoryBar groupId={data?.groupId} projectId={data?.id} height="250px" maxDisplayCount={4} environmentId={data?.environmentId} />

                      </Box>
                    </>
                  }
                />
              </Box>)
            },
          },
          {
            accessorKey: "actions",
            header: "Actions",
            size: 150,
            Cell: (params: any) => {
              const data = params?.row?.original;
              return (
              <Box sx={{ display: "flex", justifyContent: "space-between", minWidth: "10em" }}>
                
                                        
                <Box sx={{ display: "flex", justifyContent: "start" }}>
                        
                <ModalWrapper maxWidth="lg" title="" 
                      trigger={
                        <Tooltip title='Settings'>
                         
                            <IconButton ><Settings /></IconButton>
                            </Tooltip>
                       
                      } 
                      child={<ApiModalContent assetId={data?.assetId} />} 
                    />
                        
               
                  {data?.assetType === 'API_KIND_OPENAPI'
                    ? <ConfigTablePopup assetId={data?.assetId} defaultProjectID={data?.id} trigger={<Tooltip title="Analyze"><IconButton ><PlayCircle /></IconButton></Tooltip>} /> :
                    <ScannersPopup assetId={data?.assetId} assetName={data?.assetName} projectId={data?.id} environmentId={data?.environmentId === 'default' ? '' : data?.environmentId} trigger={<Tooltip title="Analyze"><IconButton ><PlayCircle /></IconButton></Tooltip>} />
                  }
                  {data?.assetType === "API_KIND_OPENAPI" ?
                    <DownloadPostmanPopup assetId={data?.assetId} trigger={
                      <IconButton ><Download /></IconButton>} /> : <div></div>}
                </Box>

                <CommonPopper
                  elevatePopper
                  trigger={<IconButton
                  >
                    <MoreVert onClick={()=>fetchProjectDetails(params?.row?.original?.id)}/>
                  </IconButton>}

                  child={
                  <Box sx={{ py: 1 }}>

                          <ModalWrapper
                            maxWidth="lg"
                            title={`Edit ${data?.name}`}
                            trigger={

                              <MenuItem onClick={() =>
                                navigate.push(
                                  `/api/${data?.assetId}/details?environmentId=${data?.environmentId}`
                                )
                              }>
                                <ListItemIcon>
                                  <IconButton>
                                  <BorderColorOutlined />
                                  </IconButton>
                                </ListItemIcon>
                                <ListItemText>Edit Project</ListItemText>
                              </MenuItem>
                                // <Chip
                                  
                                //     variant="outlined"

                                //     icon={<Tooltip title="Edit"><BorderColorOutlined /></Tooltip>}


                                //     sx={{ border: "none", color: "text.primary"}}
                                //     onClick={() => { }}
                                // />
                            }
                            child={<AddProjectForm

                                defaultFormValues={{
                                    project: data?.name,
                                }}
                                callback={() => {
                                    window.location.reload()
                                }}
                                groupId={
                                    //@ts-ignore
                                    selectedGroup?.id}
                                updateOnly
                                defaultAllowEdit={true}
                                defaultAllowDelete={true}

                                updateUUID={data?.id}
                            />}
                        />
                        <ModalWrapper
                            maxWidth="lg"
                            title={`Environments - ${data?.name}`}
                            trigger={
                              <MenuItem >
                                <ListItemIcon>
                                  <IconButton>
                                  <Terminal />
                                  </IconButton>
                                </ListItemIcon>
                                <ListItemText>Environments</ListItemText>
                              </MenuItem>

                                // <Chip
                                    
                                //     variant="outlined"
                                //     icon={<Tooltip title="Environments"><</Tooltip>}
                                //     sx={{ border: "none", color: "text.primary"}}
                                //     onClick={() => { }}
                                // />
                            }
                            child={<EnvironmentsTable projectId={data?.id} />}
                        />

                        <ModalWrapper
                            maxWidth="sm"
                            title={jiraDetails?.serverId ? 'Jira Details' : 'Link To Jira'}
                            trigger={
                              <MenuItem >
                              <ListItemIcon>
                                  <IconButton>
                                  <CloudIcon />
                                  </IconButton>
                                </ListItemIcon>
                              <ListItemText>
                                Jira Details
                                </ListItemText>
                              </MenuItem>
                            }
                            child={<LinkProjectToJira projectId={data?.id} />}
                        />
                  </Box>
                  }
                />
              </Box>
              )
            },
          },
          {
            accessorKey: "faultGroups",
            header: "Fault Groups",
            Cell: (params: any) => {
              const faultGroupInsights = params?.row?.original?.faultGroupInsights;
              const faultGroupArray = Object.keys(faultGroupInsights || {}).filter(
                (key) => faultGroupInsights?.[key]
              );
              return (
                <FaultGroups faultGroupArray={faultGroupArray} />
              )


            }
          },

        ]}
        data={rows}
        initialState={{
          density: "compact",
          columnPinning: { left: ['name'] }
        }}
        enableColumnActions={false}
        enableSorting={false}
        enableStickyHeader


        enableTopToolbar={false}

        enablePagination={false}
        enableDensityToggle={false}
        enableColumnFilters={false}
        // enableRowVirtualization
        state={{
          showProgressBars: isFetching,
        }}
        muiTableContainerProps={{
          ref: tableContainerRef, //get access to the table container element
          sx: { maxHeight: "calc(100vh - 200px)" }, //give the table a max height
          onScroll: (
            event: UIEvent<HTMLDivElement> //add an event listener to the table container element
          ) => {
            if (!disableInfiniteScroll) {
              fetchMoreOnBottomReached(event.target as HTMLDivElement)
            }

          }
        }}
      />
    </Card>
  );
};

export default ProjectTable;
